import {
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { Router } from '@angular/router';
import { Lightbox } from 'ngx-lightbox';
import { Observable } from 'rxjs';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import DateHelper from 'src/app/helpers/date-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import { SubProjectControllerRequestService } from 'src/app/services/api/sub-project-controller-request.service';
import { FilterStoreService } from 'src/app/services/filter-store.service';
import {
  ModalService,
  ObservableHandlerPair,
} from 'src/app/services/modal.service';
import { PageIndexService } from 'src/app/services/page-index-service';
import { SnackService } from 'src/app/services/snack.service';
import { CreateEditTaskModalComponent } from '../../task/create-edit-task/create-edit-task-modal.component';
import { AbstractImmoOverviewComponent } from '../abstract-immo-overview.component';
import {
  OverviewImmoTask,
  OverviewImmoTaskAsset,
} from './model/overview-immo-task';
import { ImmoTaskOverviewControllerRequestService } from './service/immo-task-overview-controller-request.service';

@Component({
  selector: 'immo-task-overview',
  templateUrl: '../abstract-immo-overview.component.html',
  styleUrls: ['../abstract-immo-overview.component.scss'],
})
export class ImmoTaskOverviewComponent
  extends AbstractImmoOverviewComponent<OverviewImmoTask>
  implements OnInit
{
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  @Input()
  taskCreatedOrEdited: Observable<never>;

  constructor(
    protected filterStoreService: FilterStoreService,
    protected pageIndexService: PageIndexService,
    protected cd: ChangeDetectorRef,
    protected subProjectService: SubProjectControllerRequestService,
    protected _lightbox: Lightbox,
    protected router: Router,
    protected immoTaskOverviewControllerRequestService: ImmoTaskOverviewControllerRequestService,
    private modalService: ModalService,
    private snackService: SnackService
  ) {
    super(
      [
        {
          key: 'id',
          active: false,
          strictEquality: true,
          numeric: true,
        },
        {
          key: 'createdAtDateTime',
          active: false,
          strictEquality: true,
          numeric: false,
        },
        {
          key: 'taskRequestingUser',
          active: false,
          strictEquality: true,
          numeric: false,
        },
      ],
      filterStoreService,
      pageIndexService,
      cd,
      subProjectService,
      _lightbox,
      router
    );
  }

  ngOnInit(): void {
    super.ngOnInit();
    if (this.taskCreatedOrEdited != null) {
      this.addSubscription(this.taskCreatedOrEdited, () => this.fetchItems());
    }
    this.hasActionButtons = true;
  }

  protected getPaginator(): MatPaginator {
    return this.paginator;
  }

  protected initDisplayedColumns(): void {
    this.displayedColumns = [
      'ownId',
      'taskCreatedAt',
      'taskRequestingUser',
      'deadline',
      'placementDate',
      'taskInstaller',
      'subject',
      'taskDescription',
      'taskAddress',
      'executionFeedback',
      'taskExecuted',
      'taskExecutionDateDisplay',
      'taskPhotos',
      'dropdownActions',
    ];
  }

  public openTour(subProject: OverviewImmoTask): void {
    throw new Error('Method not implemented.');
  }
  public copyProjectId(subProject: OverviewImmoTask): void {
    throw new Error('Method not implemented.');
  }
  protected initSortSettings(): void {
    this.sortColumn = 'id';
    this.sortAscending = false;
  }

  protected fetchItems(): void {
    this.loading = true;
    this.items = [];

    this.immoTaskOverviewControllerRequestService
      .getTable(
        this.generateFilterString(),
        this.sortColumn,
        this.sortAscending,
        this.paginator.pageSize,
        this.paginator.pageIndex,
        false
      )
      .then((res) => {
        this.processPageResult(res);
        this.processItems(res.items);
        this.totalItems = res.totalItems;
        this.items = res.items;
      })
      .finally(() => (this.loading = false));
  }

  private processItems(items: OverviewImmoTask[]): void {
    if (!CollectionHelper.isPopulated(items)) {
      return;
    }
    items.forEach((i) => {
      if (i.executed) {
        i.statusClass = 'approved';
      } else if (
        !StringHelper.isEmpty(i.currentConfirmedPlacementDateDisplay)
      ) {
        if (i.executed) {
          i.statusClass = 'approved';
        } else if (i.deadline != null) {
          i.statusClass = DateHelper.parseMoment(i.deadline).isBefore(
            this.today,
            'day'
          )
            ? 'on-hold'
            : 'collab';
        } else if (i.deadlineASAP) {
          i.statusClass = 'collab';
        }
      }
    });
  }

  public openProject(task: OverviewImmoTask) {
    this.modalService.openComponentInModal(
      CreateEditTaskModalComponent,
      [
        {
          key: 'taskId',
          value: task.id,
        },
      ],
      new ObservableHandlerPair(
        (modalRef) => modalRef.componentInstance.result,
        (result) => {
          if (result === 'taskCreatedOrEdited' || result === 'taskArchived') {
            this.fetchItems();
          }
        }
      ),
      { minWidth: '97vw', height: '95vh' }
    );
  }

  protected getLocalStorageKey(): string {
    return 'ImmoTaskOverviewComponent';
  }

  public openTaskPhotosLightbox(photos: OverviewImmoTaskAsset[]): void {
    const albums = [];

    for (const photo of photos) {
      if (photo.asset != null) {
        albums.push({
          src: `/api/assets/${photo.asset.uuid}`,
          caption: photo.type != null ? photo.type.label : '',
          thumb: `/api/assets/${photo.asset.uuid}`,
        });
      }
    }

    this._lightbox.open(albums, 0);
  }

  public archive(task: OverviewImmoTask): void {
    this.modalService.confirmDialog(
      'Taak verwijderen',
      `Bent u zeker dat u taak #${task.id} wilt verwijderen?`,
      (b: boolean) => {
        if (b) {
          this.loading = true;
          this.immoTaskOverviewControllerRequestService
            .archive(task.id)
            .then(() => {
              this.snackService.showSuccess('Taak succesvol verwijderd');
              this.fetchItems();
            })
            .catch(() => (this.loading = false));
        }
      }
    );
  }
}
